import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MyAELayout } from "../components/myae/Layout";
import { useAuth } from "../context/AuthContext";
import {
  listGroups, createGroup, uploadGroupBrief, generatePlan, applyPlan,
  uploadFile, errorMessage,
} from "../lib/api";
import { toast } from "sonner";
import {
  Users, Plus, ArrowRight, Loader2, Clock, CheckCircle2, X,
  Sparkles, Wand2, FileText, Upload, Info, ChevronDown, MessageSquare,
} from "lucide-react";
import WhatsAppImportModal from "../components/groups/WhatsAppImportModal";

const DEFAULT_SECTIONS = [
  "Introduction",
  "Literature Review",
  "Methodology",
  "Results",
  "Discussion",
  "Conclusion",
];

const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—";

const StatusBadge = ({ status }) => {
  const map = {
    collecting: ["bg-amber-100 text-amber-800", "Collecting sections"],
    ready: ["bg-indigo-100 text-indigo-700", "Analysing"],
    analyzed: ["bg-emerald-100 text-emerald-800", "Ready to review"],
    final: ["bg-emerald-100 text-emerald-800", "Finalised"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-600", status];
  return <span className={`text-[11px] font-medium px-2 py-1 rounded-full ${cls}`}>{label}</span>;
};

const ComplexityChip = ({ level }) => {
  const map = {
    Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Medium: "bg-amber-50 text-amber-700 border-amber-200",
    High: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${map[level] || "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>{level || "—"}</span>;
};

const CreateGroupModal = ({ onClose, onCreate }) => {
  // step: "basics" → "mode" → "brief" (only if planner) → "planner-result" → "manual-sections" → "review"
  const [step, setStep] = useState("basics");
  const [title, setTitle] = useState("");
  const [module, setModule] = useState("");
  const [maxMembers, setMaxMembers] = useState(6);
  const [deadline, setDeadline] = useState("");
  const [mode, setMode] = useState("");   // "planner" or "manual"
  const [briefText, setBriefText] = useState("");
  const [briefFilename, setBriefFilename] = useState("");
  const [manualPartsCount, setManualPartsCount] = useState(4);
  const [manualPartNames, setManualPartNames] = useState({}); // {index: customTitle}
  const [manualPartDescriptions, setManualPartDescriptions] = useState({}); // {index: description}
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const briefFileRef = useRef(null);

  const chooseBriefFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFile(file);
      setBriefText(up.text || "");
      setBriefFilename(file.name);
      toast.success(`Brief loaded: ${file.name}`);
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that brief."));
    } finally {
      setBusy(false);
    }
  };

  const runPlanner = async () => {
    if (!briefText.trim() || briefText.trim().length < 50) {
      return toast.error("Add the full assignment brief first (paste it, or upload the file).");
    }
    setBusy(true);
    try {
      // 1) create the group with the brief attached
      const grp = await createGroup({
        title: title.trim(),
        module: module.trim(),
        max_members: Number(maxMembers) || 6,
        deadline: deadline || "",
        sections: [{ title: "Placeholder", order: 0 }],   // temp; will be replaced by plan
        brief_text: briefText.trim(),
        brief_filename: briefFilename || "Assignment Brief.txt",
      });
      // 2) generate plan
      const p = await generatePlan(grp.id, { include_members: false });
      setPlan({ ...p, groupId: grp.id });
      setStep("planner-result");
    } catch (e) {
      toast.error(errorMessage(e, "Could not generate a plan."));
    } finally {
      setBusy(false);
    }
  };

  const applyPlannerResult = async () => {
    setBusy(true);
    try {
      // Apply the (possibly edited) plan sections
      await applyPlan(plan.groupId, {
        sections: plan.sections.map((s) => ({
          title: s.title,
          purpose: s.purpose,
          word_count: s.word_count,
          complexity: s.complexity,
          assigned_to_member_id: "",   // members not invited yet at creation time
        })),
        replace_existing: true,
      });
      toast.success("Group created with MyAE Project Planner ⭐");
      onCreate({ id: plan.groupId });
    } catch (e) {
      toast.error(errorMessage(e, "Could not apply the plan."));
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async () => {
    const count = Math.max(1, Math.min(50, Number(manualPartsCount) || 1));
    // Build sections: use custom name if user set one, else "Part N"; carry description.
    const parts = Array.from({ length: count }, (_, i) => ({
      title: (manualPartNames[i] || "").trim() || `Part ${i + 1}`,
      purpose: (manualPartDescriptions[i] || "").trim(),
      order: i,
    }));
    setBusy(true);
    try {
      const grp = await createGroup({
        title: title.trim(),
        module: module.trim(),
        max_members: Number(maxMembers) || 6,
        deadline: deadline || "",
        sections: parts,
      });
      toast.success("Group created. Invite your teammates next.");
      onCreate(grp);
    } catch (e) {
      toast.error(errorMessage(e, "Could not create group."));
    } finally {
      setBusy(false);
    }
  };

  const updatePlanSection = (i, field, val) => {
    setPlan((p) => ({
      ...p,
      sections: p.sections.map((s, idx) => idx === i ? { ...s, [field]: val } : s),
    }));
  };
  const removePlanSection = (i) =>
    setPlan((p) => ({ ...p, sections: p.sections.filter((_, idx) => idx !== i) }));
  const addPlanSection = () =>
    setPlan((p) => ({ ...p, sections: [...p.sections, { title: "", purpose: "", word_count: 500, complexity: "Medium" }] }));

  const updateManualPartName = (i, val) =>
    setManualPartNames((p) => ({ ...p, [i]: val }));

  const basicsReady = title.trim();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-group-modal" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div>
            <p className="font-display text-xl text-zinc-900">Create a group assignment</p>
            <p className="text-xs text-zinc-500 mt-1">
              {step === "basics" && "Start with the basics."}
              {step === "mode" && "How would you like to set up the sections?"}
              {step === "brief" && "Upload your assignment brief so MyAE can plan a fair structure."}
              {step === "planner-result" && "Review the plan MyAE Project Planner suggests — you can edit anything."}
              {step === "manual-sections" && "Just tell us how many parts your group has."}
            </p>
          </div>
          <button onClick={onClose} data-testid="create-group-close" className="text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
        </div>

        {/* STEP: basics */}
        {step === "basics" && (
          <>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Assignment title *</label>
                <input
                  data-testid="group-title-input"
                  className="w-full rounded-lg border border-zinc-300 px-3.5 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                  placeholder="e.g. Marketing Strategy Report — Group 4"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Module code</label>
                  <input data-testid="group-module-input" className="w-full rounded-lg border border-zinc-300 px-3.5 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none" placeholder="e.g. BUS3400" value={module} onChange={(e) => setModule(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Max members</label>
                  <input type="number" min="2" max="50" data-testid="group-max-input" className="w-full rounded-lg border border-zinc-300 px-3.5 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none" value={maxMembers} onChange={(e) => setMaxMembers(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Deadline (optional)</label>
                  <input type="date" data-testid="group-deadline-input" className="w-full rounded-lg border border-zinc-300 px-3.5 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="border-t border-zinc-200 px-6 py-4 flex justify-end gap-3 bg-zinc-50">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button
                disabled={!basicsReady}
                onClick={() => setStep("mode")}
                data-testid="basics-next-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                Next <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}

        {/* STEP: mode picker */}
        {step === "mode" && (
          <>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => { setMode("planner"); setStep("brief"); }}
                data-testid="mode-planner-btn"
                className="text-left rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5 hover:border-indigo-400 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="rounded-full bg-indigo-600 text-white p-2">
                    <Sparkles size={16} />
                  </div>
                  <p className="font-display text-lg text-zinc-900">MyAE Project Planner</p>
                  <span className="text-[10px] font-bold text-amber-500 -mt-3">★</span>
                </div>
                <p className="text-xs text-zinc-600 mb-3">Upload your brief and MyAE will:</p>
                <ul className="text-xs text-zinc-700 space-y-1.5">
                  <li className="flex items-start gap-2"><span className="text-indigo-500 mt-0.5">•</span> Break the assignment into logical sections</li>
                  <li className="flex items-start gap-2"><span className="text-indigo-500 mt-0.5">•</span> Estimate word count and complexity per section</li>
                  <li className="flex items-start gap-2"><span className="text-indigo-500 mt-0.5">•</span> Suggest a fair split across your teammates</li>
                  <li className="flex items-start gap-2"><span className="text-indigo-500 mt-0.5">•</span> Flag if anyone&rsquo;s workload looks lopsided</li>
                </ul>
                <p className="mt-3 text-[11px] font-semibold text-indigo-600 flex items-center gap-1">Recommended <ArrowRight size={11} /></p>
              </button>

              <button
                onClick={() => { setMode("manual"); setStep("manual-sections"); }}
                data-testid="mode-manual-btn"
                className="text-left rounded-2xl border border-zinc-200 bg-white p-5 hover:border-zinc-400 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="rounded-full bg-zinc-100 text-zinc-700 p-2">
                    <FileText size={16} />
                  </div>
                  <p className="font-display text-lg text-zinc-900">Manual Setup</p>
                </div>
                <p className="text-xs text-zinc-600 mb-3">Your group already knows who does what.</p>
                <ul className="text-xs text-zinc-700 space-y-1.5">
                  <li className="flex items-start gap-2"><span className="text-zinc-400 mt-0.5">•</span> Just pick how many parts your group has</li>
                  <li className="flex items-start gap-2"><span className="text-zinc-400 mt-0.5">•</span> Assign members however you like</li>
                  <li className="flex items-start gap-2"><span className="text-zinc-400 mt-0.5">•</span> MyAE uses your brief to understand each part</li>
                </ul>
              </button>
            </div>
            <div className="border-t border-zinc-200 px-6 py-4 flex justify-between items-center bg-zinc-50">
              <button onClick={() => setStep("basics")} className="text-sm text-zinc-600 hover:text-zinc-900">← Back</button>
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900">Cancel</button>
            </div>
          </>
        )}

        {/* STEP: brief upload (planner mode) */}
        {step === "brief" && (
          <>
            <div className="p-6 space-y-4">
              <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 flex items-start gap-2">
                <Info size={14} className="text-indigo-600 mt-0.5 shrink-0" />
                <p className="text-xs text-indigo-900">Your brief becomes the shared reference point for every member&rsquo;s individual analysis AND the overall final analysis — so feedback stays consistent across the whole group.</p>
              </div>
              <div>
                <input
                  ref={briefFileRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt,.rtf,.md"
                  className="hidden"
                  onChange={(e) => chooseBriefFile(e.target.files?.[0])}
                  data-testid="brief-file-input"
                />
                <button
                  onClick={() => briefFileRef.current?.click()}
                  disabled={busy}
                  data-testid="brief-choose-file"
                  className="w-full rounded-lg border-2 border-dashed border-zinc-300 hover:border-indigo-400 py-6 text-sm text-zinc-600 flex flex-col items-center gap-2 transition-colors"
                >
                  <Upload size={22} className="text-zinc-400" />
                  <span>{briefFilename ? `Selected: ${briefFilename}` : "Click to upload your brief (DOCX / PDF / TXT)"}</span>
                </button>
              </div>
              <div className="relative flex items-center">
                <div className="flex-1 border-t border-zinc-200" />
                <span className="px-3 text-xs text-zinc-400">or paste the brief text below</span>
                <div className="flex-1 border-t border-zinc-200" />
              </div>
              <textarea
                data-testid="brief-text"
                className="w-full min-h-[220px] rounded-lg border border-zinc-300 p-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                placeholder="Paste your assignment brief here…"
                value={briefText}
                onChange={(e) => { setBriefText(e.target.value); if (briefFilename && !e.target.value.trim()) setBriefFilename(""); }}
              />
              <p className="text-xs text-zinc-500">{(briefText.trim().match(/\b\w+\b/g) || []).length} words</p>
            </div>
            <div className="border-t border-zinc-200 px-6 py-4 flex justify-between items-center bg-zinc-50">
              <button onClick={() => setStep("mode")} className="text-sm text-zinc-600 hover:text-zinc-900">← Back</button>
              <div className="flex gap-3">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900">Cancel</button>
                <button
                  onClick={runPlanner}
                  disabled={busy || briefText.trim().length < 50}
                  data-testid="run-planner-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Generate plan with MyAE ⭐
                </button>
              </div>
            </div>
          </>
        )}

        {/* STEP: planner result */}
        {step === "planner-result" && plan && (
          <>
            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Sparkles size={12} /> MyAE Project Planner detected
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-2">
                  <div>
                    <p className="text-[10px] uppercase text-zinc-500">Assignment</p>
                    <p className="text-sm font-medium text-zinc-900">{plan.detected_title || "Untitled"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-zinc-500">Type</p>
                    <p className="text-sm font-medium text-zinc-900">{plan.detected_assessment_type || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-zinc-500">Target words</p>
                    <p className="text-sm font-medium text-zinc-900 tabular-nums">{plan.detected_word_count || "—"}</p>
                  </div>
                </div>
                {plan.rationale && <p className="text-xs text-zinc-700 italic">{plan.rationale}</p>}
              </div>

              {plan.fairness_warning && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="fairness-warning">
                  ⚠ {plan.fairness_warning}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Sections ({plan.sections.length})</p>
                  <button onClick={addPlanSection} data-testid="planner-add-section" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">+ Add section</button>
                </div>
                <div className="space-y-2">
                  {plan.sections.map((s, i) => (
                    <div key={i} className="rounded-lg border border-zinc-200 bg-white p-3" data-testid={`planner-section-${i}`}>
                      <div className="flex items-start gap-2">
                        <span className="text-xs text-zinc-400 font-mono w-6 mt-2">{i + 1}.</span>
                        <div className="flex-1 space-y-2">
                          <div className="flex gap-2 items-start">
                            <input
                              className="flex-1 rounded-md border border-zinc-300 px-2.5 py-1.5 text-sm font-medium focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                              placeholder="Section title"
                              data-testid={`planner-section-title-${i}`}
                              value={s.title}
                              onChange={(e) => updatePlanSection(i, "title", e.target.value)}
                            />
                            <input
                              type="number"
                              className="w-24 rounded-md border border-zinc-300 px-2 py-1.5 text-xs focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none tabular-nums"
                              placeholder="words"
                              data-testid={`planner-section-words-${i}`}
                              value={s.word_count || ""}
                              onChange={(e) => updatePlanSection(i, "word_count", Number(e.target.value) || 0)}
                            />
                            <select
                              className="rounded-md border border-zinc-300 px-2 py-1.5 text-xs bg-white focus:border-indigo-500 outline-none"
                              value={s.complexity || "Medium"}
                              data-testid={`planner-section-complexity-${i}`}
                              onChange={(e) => updatePlanSection(i, "complexity", e.target.value)}
                            >
                              <option value="Low">Low</option>
                              <option value="Medium">Medium</option>
                              <option value="High">High</option>
                            </select>
                            <button onClick={() => removePlanSection(i)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`planner-section-remove-${i}`}><X size={14} /></button>
                          </div>
                          {s.purpose && (
                            <p className="text-xs text-zinc-500 pl-0.5">{s.purpose}</p>
                          )}
                          <div className="flex items-center gap-1.5">
                            <ComplexityChip level={s.complexity} />
                            <span className="text-[11px] text-zinc-500 tabular-nums">~{s.word_count || 0} words</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-t border-zinc-200 px-6 py-4 flex justify-between items-center bg-zinc-50">
              <button onClick={() => setStep("brief")} className="text-sm text-zinc-600 hover:text-zinc-900">← Back</button>
              <div className="flex gap-3">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900">Cancel</button>
                <button
                  onClick={applyPlannerResult}
                  disabled={busy || plan.sections.length === 0}
                  data-testid="apply-plan-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Apply plan and create group
                </button>
              </div>
            </div>
          </>
        )}

        {/* STEP: manual sections */}
        {step === "manual-sections" && (
          <>
            <div className="p-6 space-y-5" data-testid="manual-sections-step">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">How many parts does your group have? *</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setManualPartsCount((n) => Math.max(1, Number(n) - 1))}
                    data-testid="parts-count-decrement"
                    className="w-10 h-10 rounded-lg border border-zinc-300 bg-white hover:border-indigo-400 text-lg font-semibold text-zinc-700 disabled:opacity-40"
                    disabled={Number(manualPartsCount) <= 1}
                    aria-label="Fewer parts"
                  >−</button>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    data-testid="parts-count-input"
                    className="w-20 rounded-lg border border-zinc-300 px-3 py-2 text-center text-xl font-display font-semibold tabular-nums focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                    value={manualPartsCount}
                    onChange={(e) => setManualPartsCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                  />
                  <button
                    onClick={() => setManualPartsCount((n) => Math.min(50, Number(n) + 1))}
                    data-testid="parts-count-increment"
                    className="w-10 h-10 rounded-lg border border-zinc-300 bg-white hover:border-indigo-400 text-lg font-semibold text-zinc-700 disabled:opacity-40"
                    disabled={Number(manualPartsCount) >= 50}
                    aria-label="More parts"
                  >+</button>
                  <span className="text-xs text-zinc-500 ml-2">
                    MyAE will use your assignment brief to understand what each part covers &mdash; no need to name them.
                  </span>
                </div>
              </div>

              {/* Preview */}
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4" data-testid="parts-preview">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-2">Your group will have</p>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: Math.max(1, Math.min(50, Number(manualPartsCount) || 1)) }).map((_, i) => {
                    const displayed = (manualPartNames[i] || "").trim() || `Part ${i + 1}`;
                    return (
                      <span
                        key={i}
                        data-testid={`parts-preview-${i}`}
                        className="rounded-full bg-white border border-zinc-200 px-3 py-1 text-xs font-medium text-zinc-800"
                      >
                        {displayed}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Per-part rename + description (always visible, compact) */}
              <div>
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Describe each part (optional)</p>
                <p className="text-xs text-zinc-500 mb-3">
                  Give each part a short description so every member knows what belongs there. Rename the part title too if it helps &mdash; otherwise MyAE keeps &ldquo;Part 1&rdquo;, &ldquo;Part 2&rdquo;&hellip;
                </p>
                <div className="space-y-3" data-testid="parts-editor">
                  {Array.from({ length: Math.max(1, Math.min(50, Number(manualPartsCount) || 1)) }).map((_, i) => (
                    <div key={i} className="rounded-lg border border-zinc-200 bg-white p-3" data-testid={`part-editor-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Part {i + 1}</span>
                        <input
                          data-testid={`part-name-input-${i}`}
                          className="flex-1 rounded-md border border-zinc-200 px-2.5 py-1 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                          placeholder={`Part ${i + 1} name (optional)`}
                          value={manualPartNames[i] || ""}
                          onChange={(e) => updateManualPartName(i, e.target.value)}
                        />
                      </div>
                      <input
                        data-testid={`part-description-input-${i}`}
                        className="w-full rounded-md border border-zinc-200 px-2.5 py-1.5 text-sm text-zinc-800 placeholder:text-zinc-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                        placeholder={i === 0 ? "e.g. Introduction & Title" : i === 1 ? "e.g. Background / Context" : `Description for Part ${i + 1}`}
                        value={manualPartDescriptions[i] || ""}
                        onChange={(e) => setManualPartDescriptions((p) => ({ ...p, [i]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 flex items-start gap-2">
                <Info size={14} className="text-indigo-600 mt-0.5 shrink-0" />
                <p className="text-xs text-indigo-900">
                  Upload your <b>assignment brief</b> on the group page next &mdash; MyAE uses it to understand what each part covers, so members don&rsquo;t need to name their sections.
                </p>
              </div>
            </div>
            <div className="border-t border-zinc-200 px-6 py-4 flex justify-between items-center bg-zinc-50">
              <button onClick={() => setStep("mode")} className="text-sm text-zinc-600 hover:text-zinc-900">← Back</button>
              <div className="flex gap-3">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900">Cancel</button>
                <button
                  onClick={submitManual}
                  disabled={busy}
                  data-testid="create-group-submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create group
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function Groups() {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [items, setItems] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  const refresh = () => listGroups().then(setItems).catch(() => setItems([]));

  useEffect(() => {
    if (!user) return;
    refresh();
  }, [user]);

  if (!user) {
    return (
      <MyAELayout>
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Users size={48} className="mx-auto text-indigo-400 mb-4" />
          <h1 className="font-display text-3xl text-zinc-900 mb-2">Group Assignments</h1>
          <p className="text-zinc-600 mb-6">Sign in to create or join a group assignment.</p>
          <button
            onClick={() => setAuthOpen(true)}
            data-testid="groups-signin"
            className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 text-white px-6 py-3 text-sm font-semibold hover:bg-zinc-800"
          >
            Sign in to continue
          </button>
        </div>
      </MyAELayout>
    );
  }

  return (
    <MyAELayout>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="groups-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-1">New</p>
            <h1 className="font-display text-4xl text-zinc-900 tracking-tight">Group Assignments</h1>
            <p className="text-zinc-600 mt-2 max-w-2xl">
              Collaborate on assignments the way universities expect. Each teammate writes and analyses their section, then MyAE stitches everything together into one polished, submission-ready document with an overall predicted grade.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowWhatsApp(true)}
              data-testid="whatsapp-import-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-white text-emerald-700 border-2 border-emerald-500 px-4 py-2.5 text-sm font-semibold hover:bg-emerald-50 whitespace-nowrap"
              title="Build a group from a WhatsApp chat or screenshot"
            >
              <MessageSquare size={15} /> Import from WhatsApp
            </button>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="create-group-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-indigo-700 whitespace-nowrap"
            >
              <Plus size={16} /> New group
            </button>
          </div>
        </div>

        {items === null ? (
          <div className="flex items-center gap-3 text-zinc-500">
            <Loader2 size={18} className="animate-spin" /> Loading groups…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-12 text-center" data-testid="groups-empty">
            <Users size={40} className="mx-auto text-zinc-300 mb-3" />
            <p className="font-display text-xl text-zinc-900">No groups yet</p>
            <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">
              Start a group assignment, add your teammates, and let MyAE bring your sections together automatically once everyone has uploaded.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="create-group-empty-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-indigo-700"
            >
              <Plus size={16} /> Create your first group
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((g, i) => (
              <motion.button
                key={g.id}
                onClick={() => navigate(`/groups/${g.id}`)}
                data-testid={`group-card-${g.id}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="text-left rounded-2xl border border-zinc-200 bg-white p-5 hover:border-indigo-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="font-display text-lg text-zinc-900 truncate">{g.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {g.module || "No module"} · {g.is_owner ? "You own this" : "Member"}
                    </p>
                  </div>
                  <StatusBadge status={g.status} />
                </div>

                <div className="grid grid-cols-3 gap-3 text-xs text-zinc-600">
                  <div>
                    <p className="text-zinc-400 uppercase tracking-wide text-[10px] font-semibold">Sections</p>
                    <p className="mt-0.5 font-medium text-zinc-900 tabular-nums">
                      {g.sections_uploaded}/{g.sections_total}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 uppercase tracking-wide text-[10px] font-semibold">Members</p>
                    <p className="mt-0.5 font-medium text-zinc-900 tabular-nums">
                      {g.members_count}/{g.max_members}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 uppercase tracking-wide text-[10px] font-semibold">Deadline</p>
                    <p className="mt-0.5 font-medium text-zinc-900">{g.deadline ? fmtDate(g.deadline) : "—"}</p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-3">
                  <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                    {g.status === "collecting" ? (
                      <>
                        <Clock size={12} /> {g.sections_total - g.sections_uploaded} to go
                      </>
                    ) : g.overall_analysis_id ? (
                      <>
                        <CheckCircle2 size={12} className="text-emerald-600" /> Overall analysis ready
                      </>
                    ) : (
                      <span>Started {fmtDate(g.created_at)}</span>
                    )}
                  </div>
                  <ArrowRight size={16} className="text-zinc-400" />
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateGroupModal
          onClose={() => setShowCreate(false)}
          onCreate={(g) => {
            setShowCreate(false);
            navigate(`/groups/${g.id}`);
          }}
        />
      )}
      {showWhatsApp && (
        <WhatsAppImportModal
          onClose={() => setShowWhatsApp(false)}
          onCreated={(g) => {
            setShowWhatsApp(false);
            navigate(`/groups/${g.id}`);
          }}
        />
      )}
    </MyAELayout>
  );
}
